"use client"

import Publish from "@/(admin)/modules/letaky/pages/Publish"
import StoreFormFields from "@/(admin)/modules/stores/components/StoreFormFields"
import AccessDenied from "@/components/custom-access-denied"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { useStoresAdmin } from "@/hook/useStoresAdmin"
import { t } from "@/languages/i18n"
import { MediaData } from "@/lib/config/types"
import { BlocksImageWithTitleComponent, BlocksTimeRangeComponent, Store, StoreRequest } from "@/types/codegen/data-contracts"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { FormSection, FormShell, FormSkeleton } from "@/(admin)/components/form"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import { STORES } from "../../../../constants/admin-paths"

type SingleImage = NonNullable<NonNullable<Store['galeryPhotos']>['data']>[number]
type SingleCompaign = NonNullable<NonNullable<Store['campaigns']>['data']>[number]
interface ExtendedStoreRequest extends Omit<StoreRequest, 'data'> {
    data: StoreRequest['data'] & {
        publishedAt?: string | null;
    }
}

interface FormUpsertStoreProps {
    onStoreAdded?: () => void
}

const FormUpsertStore: React.FC<FormUpsertStoreProps> = ({ onStoreAdded }) => {
    const searchParams = useSearchParams()
    const id = searchParams.get("id")
    const [status, setStatus] = useState("Draft")
    const [publishDate, setPublishDate] = useState<string | null>(null)
    const [language, setLanguages] = useState("vi")
    const router = useRouter()
    const isEditMode = Boolean(id)
    const [form] = Form.useForm()
    const [managerImages, setManagerImages] = useState<Record<number, MediaData[]>>({});
    const { hasPermission: checkPermission } = useCheckPermission()

    const {
        loading, storeDetail,
        actions: { getStoreInformation, updateStore, createStore, hasPermission },
    } = useStoresAdmin()

    // Check permission to access the page
    if (!checkPermission("store", isEditMode ? "update" : "create")) {
        return <AccessDenied />
    }

    const [featuredImageValue, setFeaturedImageValue] = useState<MediaData[]>([])
    const [galleryImagesValue, setGalleryImagesValue] = useState<MediaData[]>([])
    const [isFormReady, setIsFormReady] = useState(false);
    const getNormalizedSnapshot = useCallback(() => {
        const values = form.getFieldsValue(true)


        const rawSnapshot = {
            ...values,


            galeryPhotos: Array.isArray(values.galeryPhotos) ? [...values.galeryPhotos].sort() : [],
            campaigns: Array.isArray(values.campaigns) ? [...values.campaigns].sort() : [],


            managers: (values.managers || []).map((m: any) => ({
                name: m.name,
                url: m.url,
                target: m.target || "_self",
                image: m.image,
            })),


            timeRange: (values.timeRange || []).map((t: any) => ({
                day: t.day,
                from: t.from ? t.from.substring(0, 5) : null,
                to: t.to ? t.to.substring(0, 5) : null,
            })),


            status,
            language,
            publishDate
        }


        return normalizeFormData(rawSnapshot)
    }, [form, status, language, publishDate])

    const { isChange, checkChange, commitSnapshot, resetSnapshot, modal, message, handleCancel: guardedHandleCancel } = useGuardedForm({
        form,
        getSnapshot: getNormalizedSnapshot
    })

    // Switching locale keeps this component mounted (same route, different ?id).
    // Drop the previous record's snapshot so the incoming one commits a fresh baseline.
    useEffect(() => {
        if (!isEditMode) return
        setIsFormReady(false)
        resetSnapshot()
    }, [id, isEditMode, resetSnapshot])

    useEffect(() => {
        checkChange()
    }, [status, language, publishDate, checkChange])

    useEffect(() => {
        if (isEditMode && storeDetail) {
            const attr = storeDetail?.attributes
            const imgMap: Record<number, MediaData[]> = {};
            const formManagers = attr?.managers?.map((manager: BlocksImageWithTitleComponent, index: number) => {
                if (manager.image?.data) {
                    imgMap[index] = [manager.image.data as MediaData];
                }
                return {
                    name: manager.name,
                    url: manager.url,
                    target: manager.target,
                    image: manager.image?.data?.id
                };
            }) || [];
            form.setFieldsValue({
                ...attr,
                image: attr?.image?.data?.id,
                galeryPhotos: attr?.galeryPhotos?.data?.map((item: SingleImage) => item.id),
                campaigns: attr?.campaigns?.data?.map((campaign: SingleCompaign) => campaign.id) || [],
                managers: formManagers
            })




            setStatus(attr?.publishedAt ? "Published" : "Draft");
            if (attr?.locale) setLanguages(attr?.locale);
            setManagerImages(imgMap);
            if (attr?.image?.data) setFeaturedImageValue([attr?.image.data as MediaData]);
            if (attr?.galeryPhotos?.data) setGalleryImagesValue(attr?.galeryPhotos.data as MediaData[]);
            setIsFormReady(true);
        }
    }, [storeDetail, isEditMode, form])

    useEffect(() => {
        if (!isEditMode) {
            setIsFormReady(true);
        }
    }, [isEditMode]);

    useEffect(() => {
        if (isFormReady) {
            const animationId = requestAnimationFrame(() => {
                commitSnapshot();
            });
            return () => cancelAnimationFrame(animationId);
        }
    }, [isFormReady, commitSnapshot]);

    const handleSubmit = async () => {
        try {
            const values = await form.getFieldsValue()
            const cleanTimeRange = values.timeRange?.map((timeRange: BlocksTimeRangeComponent) => ({
                day: timeRange.day,
                from: timeRange.from ? (timeRange.from.length === 5 ? `${timeRange.from}:00` : timeRange.from) : null,
                to: timeRange.to ? (timeRange.to.length === 5 ? `${timeRange.to}:00` : timeRange.to) : null,
            })) || [];
            const requestData: ExtendedStoreRequest = {
                data: {
                    name: values.name,
                    address: values.address,
                    email: values.email,
                    phone: values.phone,
                    longitude: Number(values.longitude),
                    latitude: Number(values.latitude),
                    googleMapCode: values.googleMapCode,
                    guidance: values.guidance,
                    description: values.description,
                    managers: values.managers,
                    image: values.image,
                    galeryPhotos: values.galeryPhotos,
                    timeRange: cleanTimeRange,
                    locale: language,
                    publishedAt: status === "Draft" ? null : (publishDate || new Date().toISOString()),
                    campaigns: values.campaigns,
                }
            }

            if (isEditMode) {
                await updateStore(Number(id), requestData)
                commitSnapshot()
            } else {
                const success = await createStore(requestData)
                if (success) {
                    commitSnapshot()
                    onStoreAdded?.()
                    router.push(createPath(STORES.LIST.PATH, searchParams))
                }
            }
        } catch (error) {
            console.error('Submit error:', error)
        }
    }

    useEffect(() => {
        if (id && isEditMode) {
            getStoreInformation(Number(id))
        }
    }, [id, isEditMode])

    const handleCancel = () => {
        const targetPath = createPath(STORES.LIST.PATH, searchParams)
        guardedHandleCancel(targetPath)
    }

    const localizations = useMemo(() => {
        const data = storeDetail?.attributes?.localizations?.data
        if (!Array.isArray(data)) return []
        return data
            .filter(loc => loc?.id && loc?.attributes?.locale)
            .map(loc => ({ id: loc.id as number, locale: loc.attributes!.locale as string }))
    }, [storeDetail])

    const navigateGuarded = useCallback(
        (path: string) => {
            const go = () => router.push(path)
            if (!isChange) {
                go()
                return
            }
            modal.confirm({
                title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
                content: t("Bạn có những thay đổi chưa lưu. Nếu thoát bây giờ, dữ liệu của bạn sẽ bị mất."),
                okText: t("Thoát và Bỏ qua"),
                cancelText: t("Ở lại trang"),
                okButtonProps: { danger: true },
                onOk: go,
            })
        },
        [isChange, modal, router],
    )
    const canSubmit = isEditMode ? hasPermission("update") : hasPermission("create");

    if (isEditMode && !isFormReady) {
        return <FormSkeleton mainSections={3} sidebarCards={1} />
    }

    const crumbs = [
        { title: t("Cửa hàng"), href: createPath(STORES.LIST.PATH, searchParams) },
        { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
    ]

    const main = (
        <FormSection title={t("Thông tin cửa hàng")} collapsible={false}>
            <StoreFormFields
                form={form}
                featuredImageValue={featuredImageValue}
                setFeaturedImageValue={setFeaturedImageValue}
                galleryImagesValue={galleryImagesValue}
                setGalleryImagesValue={setGalleryImagesValue}
                managerImages={managerImages}
                setManagerImages={(index, val) => {
                    setManagerImages(prev => ({
                        ...prev,
                        [index]: val,
                    }))
                }}
            />
        </FormSection>
    )

    const sidebar = (
        <Publish
            onSubmit={form.submit}
            isCreateEdit={!isEditMode}
            isEditMode={isEditMode}
            setStatus={setStatus}
            status={status}
            setPublishDate={setPublishDate}
            language={language}
            setLanguages={setLanguages}
            loading={loading}
            canSubmit={canSubmit}
            onCancle={handleCancel}
            localizations={localizations}
            onNavigateToLocale={locId => navigateGuarded(`/admin${STORES.EDIT.PATH}?id=${locId}`)}
            onCreateLocale={targetLocale => navigateGuarded(`/admin/stores/translate?id=${id}&locale=${targetLocale}`)}
        />
    )

    return (
        <Form labelAlign="left" layout="vertical" form={form} onFinish={handleSubmit} onValuesChange={checkChange}>
            <FormShell
                title={isEditMode ? t("Cập nhật cửa hàng") : t("Tạo cửa hàng")}
                crumbs={crumbs}
                main={main}
                sidebar={sidebar}
            />
        </Form>
    );
}

export default FormUpsertStore;